import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { TIMER_MODES } from "../../utils/planningSubgroups";
import "./PlanningSubgroupBoard.css";

export function SplitSubgroupDialog({
  open,
  onClose,
  onCreate,
  tables = [],
  existingCount = 1,
  busy = false,
  /** Above nested sheets (e.g. Live Training plan sheet at 4200). */
  zIndex = 4600,
  /** Fullscreen Live Training hides dialogs portaled to document.body. */
  container = undefined,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [timerMode, setTimerMode] = useState(TIMER_MODES.INDEPENDENT);
  const [tableIds, setTableIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setName("");
    setTimerMode(TIMER_MODES.INDEPENDENT);
    setTableIds([]);
  }, [open]);

  const suggested = useMemo(
    () => `${t("subGroup") || "Sub-group"} ${existingCount}`,
    [existingCount, t]
  );

  const toggleTable = (id) => {
    setTableIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      className="psg-split-dialog"
      container={container || undefined}
      sx={{ zIndex }}
    >
      <DialogTitle>{t("splitGroup") || "Split group"}</DialogTitle>
      <DialogContent>
        <div className="psg-split">
          <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.45 }}>
            {t("splitGroupHint") ||
              "Peel some players onto their own tables. By default the station gets its own chronometer — switch to the shared clock anytime. A shared clock starts them on the first training block only."}
          </p>
          <div className="psg-split__field">
            <label htmlFor="psg-split-name">{t("subGroupName") || "Sub-group name"}</label>
            <input
              id="psg-split-name"
              className="psg-split__input"
              value={name}
              placeholder={suggested}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="psg-split__modes" role="radiogroup" aria-label={t("timerMode") || "Timer"}>
            <button
              type="button"
              className={`psg-mode${
                timerMode === TIMER_MODES.INDEPENDENT ? " psg-mode--on" : ""
              }`}
              onClick={() => setTimerMode(TIMER_MODES.INDEPENDENT)}
            >
              <h4>{t("ownTimer") || "Own timer"}</h4>
              <p>
                {t("timerIndependentHint") ||
                  "Start, pause and advance this station on its own."}
              </p>
            </button>
            <button
              type="button"
              className={`psg-mode${timerMode === TIMER_MODES.SYNCED ? " psg-mode--on" : ""}`}
              onClick={() => setTimerMode(TIMER_MODES.SYNCED)}
            >
              <h4>{t("sharedTimer") || "Same timer as main"}</h4>
              <p>
                {t("timerSyncedHint") ||
                  "Shares the main clock and starts on the first training block. You can change this anytime."}
              </p>
            </button>
          </div>
          {tables.length > 0 ? (
            <div className="psg-split__field">
              <label>{t("assignTables") || "Tables for this station"}</label>
              <div className="psg-tables">
                {tables.map((tbl) => {
                  const id = tbl.table_id;
                  const on = tableIds.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`psg-table-chip${on ? " psg-table-chip--on" : ""}`}
                      onClick={() => toggleTable(id)}
                    >
                      {t("table") || "Table"} {tbl.table_number ?? ""}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("cancel") || "Cancel"}
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() =>
            onCreate?.({
              name: name.trim() || suggested,
              timer_mode: timerMode,
              table_ids: tableIds,
            })
          }
        >
          {t("createSubGroup") || "Create station"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SplitSubgroupDialog;
